import { fieldConfig } from "@autoform/zod";
import * as z from "zod";
import { zNumber } from "../common/schemaHelpers";

// ---------------------------------------------------------------------------
// Test Parameters — Digitizer
// ---------------------------------------------------------------------------

const digitizerSchema = z.object({
	time: z.string().default("30").describe("Test Duration (seconds)"),
	accuracy: z.string().default("0.95").describe("% of Required Swipe"),
	minFingerRadius: zNumber().default(0.5).describe("Minimum Finger Radius"),
	maxFingerRadius: zNumber().default(1.5).describe("Maximum Finger Radius"),
});

// ---------------------------------------------------------------------------
// Test Parameters — Mirror & Peer
// ---------------------------------------------------------------------------

const brightnessCheckSchema = z.object({
	tooDarkLimit: zNumber().default(0).describe("Too Dark Limit"),
	tooBrightLimit: zNumber().default(1).describe("Too Bright Limit"),
});

const mirrorPeerSchema = z.object({
	brightnessCheck: brightnessCheckSchema.prefault({}).describe("Brightness Check"),
	highResolutionImages: z.boolean().default(false).describe("High Resolution Grader Images"),
	captureDelaySecs: zNumber().default(3).describe("Capture Timer Delay in Seconds"),
});

// ---------------------------------------------------------------------------
// Test Parameters — Battery
// ---------------------------------------------------------------------------

const batterySchema = z.object({
	outputMetricsToHost: z.boolean().default(false).describe("Output Detailed Metrics to Host"),
	network_stress_test_url: z
		.string()
		.default("https://jsonplaceholder.typicode.com/posts/1/photos")
		.check(
			fieldConfig({
				fieldType: "url",
				description: "Url of a dummy call for the network stress test.",
			}),
		)
		.describe("Network Stress Test URL"),
	stress_test_duration_seconds: zNumber().default(40).describe("Stress Test Duration (seconds)"),
	network_stress_test_interval: zNumber().default(2).describe("Network Stress Test Interval (seconds)"),
});

// ---------------------------------------------------------------------------
// Test Parameters — Sound
// ---------------------------------------------------------------------------

const soundTestSchema = z.object({
	retriesAllowed: zNumber().default(2).describe("Number of Retries Allowed"),
});

// ---------------------------------------------------------------------------
// Test Parameters (top-level section)
// ---------------------------------------------------------------------------

const testParamsSchema = z.object({
	digitizer: digitizerSchema.prefault({}).describe("Digitizer Parameters"),
	mirrorPeer: mirrorPeerSchema.prefault({}).describe("Parameters for Mirror and Peer Photo Capturing Flow"),
	battery: batterySchema.prefault({}).describe("Battery Test Parameters"),
	soundTest: soundTestSchema.prefault({}).describe("Sound"),
});

// ---------------------------------------------------------------------------
// Offer Page
// ---------------------------------------------------------------------------

const offerPageSchema = z.object({
	finalOfferEnabled: z.boolean().default(false).describe("Final Offer Enabled"),
	offerExpires: z.boolean().default(false).describe("Voucher Offer Expires"),
	offerExpiresIn: z.string().default("7").describe("Number of Days Until Offer Expires"),
	displayVoucherAndCashOffers: z.boolean().default(false).describe("Display Voucher and Cash Offers"),
});

// ---------------------------------------------------------------------------
// Deep Link Config
// ---------------------------------------------------------------------------

const deepLinkConfigSchema = z.object({
	iosAppIds: z.string().default("").describe("iOS App IDs"),
	iosAppUrl: z.string().default("").describe("App Store URL"),
	androidAppIds: z.string().default("").describe("Android App IDs"),
	androidAppUrl: z.string().default("").describe("Play Store URL"),
});

// ---------------------------------------------------------------------------
// Technical Config
// ---------------------------------------------------------------------------

const defaultPollingPolicySchema = z.object({
	firstPollMs: zNumber().default(8000).describe("First Poll Attempt"),
	pollIntervalMs: zNumber().default(2000).describe("Poll Interval"),
	pollingTimeoutSecs: zNumber().default(180).describe("Polling Timeout Seconds"),
});

const technicalConfigSchema = z.object({
	default_polling_policy: defaultPollingPolicySchema.prefault({}).describe("Default Polling Policy"),
	retriesLimit: zNumber().default(5).describe("Number of API Retry Times Limit"),
	ablyTimeout: zNumber().default(600).describe("Ably Timeout"),
});

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

const sessionSchema = z.object({
	expire_minutes: zNumber().default(120).describe("Minutes Until a Session Expires"),
	kickOnSessionExpiry: z.boolean().default(false).describe("Kick User on Session Expiry"),
});

// ---------------------------------------------------------------------------
// Mobile SDK Schema
// ---------------------------------------------------------------------------

export const mobileSDKShape = {
	testParams: testParamsSchema.prefault({}).describe("Test Parameters"),
	offerPage: offerPageSchema.prefault({}).describe("Offer Page Options"),
	deepLinkConfig: deepLinkConfigSchema.prefault({}).describe("Deep Link Config"),
	technical_config: technicalConfigSchema.prefault({}).describe("Technical Configurations"),
	session: sessionSchema.prefault({}).describe("Session Configuration"),
};

export const mobileSDKSchema = z.object(mobileSDKShape);

export type MobileSDKConfig = z.infer<typeof mobileSDKSchema>;
